import React, {ReactNode} from 'react'
import {useIntl} from 'react-intl'
import styled from '@emotion/styled'

import {Box} from '@vio/atlas/helpers/Box'
import {Icon} from '@vio/atlas/Icon'
import {SkeletonLoader} from '@vio/atlas/SkeletonLoader'
import {Text} from '@vio/atlas/Text'
import {cssTheme} from '@vio/atlas/themes'

import {priceTrendMessages} from '../../utils/messages'
import {
  BAR_HEIGHT,
  SEGMENT_GAP_PX,
  SEGMENT_RADIUS,
  TOOLTIP_ROW_HEIGHT
} from './constants'
import {SEGMENT_COLORS} from './PriceRangeBar'
import {SEGMENT_CENTERS} from './utils'

// Fixed tooltip chip size (per Figma spec) — icon + price skeleton sit inside
// a 78 × 32 bubble so the loading state has deterministic dimensions, instead
// of growing with the skeleton slot's natural size.
const TOOLTIP_CHIP_WIDTH = 78
const TOOLTIP_CHIP_HEIGHT = 32
const TOOLTIP_PRICE_SKELETON_WIDTH = 32
const TOOLTIP_PRICE_SKELETON_HEIGHT = 16
const BOUNDARY_SKELETON_WIDTH = 36

// Layout mirrors the live PriceRangeBar exactly — same row heights, same
// per-row margins. No flex+gap on Root because the live uses block-flow
// + per-row margin-top/margin-bottom; matching that keeps the swap from
// skeleton → live shift-free.
const Root = styled.div`
  width: 100%;
`

// Mirrors the live: tooltip-tail-to-bar distance via `margin-bottom`. The
// (-1px) compensates for the tail's `margin-top: -1px` so the visual tip
// sits 15px above the bar.
const TooltipRow = styled.div`
  position: relative;
  width: 100%;
  height: ${TOOLTIP_ROW_HEIGHT}px;
  margin-bottom: calc(${cssTheme.layout.spacing.s400} - 1px);
`

// Mirrors PriceRangeBar's TooltipAnchor + TooltipBubble + TooltipTail so the
// real tooltip shape is preserved during loading. The provider icon renders
// as real content; only the price text swaps for a skeleton.
const TooltipAnchor = styled.div`
  position: absolute;
  bottom: 0;
  left: ${SEGMENT_CENTERS.fairPct}%;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  pointer-events: none;
`

const TooltipBubble = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${cssTheme.layout.spacing.s200};
  width: ${TOOLTIP_CHIP_WIDTH}px;
  height: ${TOOLTIP_CHIP_HEIGHT}px;
  background: ${cssTheme.colors.neutral.c000};
  border-radius: ${cssTheme.layout.radius.md};
  box-shadow: ${cssTheme.shadows.floating};
  white-space: nowrap;
`

const TooltipTail = styled.div`
  width: 0;
  height: 0;
  border-left: 7px solid transparent;
  border-right: 7px solid transparent;
  border-top: 6px solid ${cssTheme.colors.neutral.c000};
  margin-top: -1px;
`

const TooltipPriceSlot = styled.div`
  width: ${TOOLTIP_PRICE_SKELETON_WIDTH}px;
  height: ${TOOLTIP_PRICE_SKELETON_HEIGHT}px;
  overflow: hidden;
  border-radius: ${cssTheme.layout.radius.sm};
`

const BarTrack = styled.div`
  display: flex;
  gap: ${SEGMENT_GAP_PX}px;
  width: 100%;
  height: ${BAR_HEIGHT}px;
`

const Segment = styled.div<{color: string; position: 'low' | 'fair' | 'high'}>`
  flex: 1 1 0;
  background-color: ${({color}) => color};
  border-top-left-radius: ${({position}) => SEGMENT_RADIUS[position].tl};
  border-top-right-radius: ${({position}) => SEGMENT_RADIUS[position].tr};
  border-bottom-right-radius: ${({position}) => SEGMENT_RADIUS[position].br};
  border-bottom-left-radius: ${({position}) => SEGMENT_RADIUS[position].bl};
`

// Mirrors PriceRangeBar's grid layout + spacing tokens so the skeleton's
// rows occupy the same pixels as the live component.
const LabelsRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  width: 100%;
  margin-top: ${cssTheme.layout.spacing.s250};
`

const Label = styled.div`
  justify-self: center;
  white-space: nowrap;
`

const PricesRow = styled.div`
  display: grid;
  width: 100%;
  margin-top: ${cssTheme.layout.spacing.s200};
`

const PriceCell = styled.div<{align: 'start' | 'end'; column?: number}>`
  grid-row: 1;
  ${({column}) => (column ? `grid-column: ${column};` : '')}
  justify-self: ${({align}) => align};
  width: ${BOUNDARY_SKELETON_WIDTH}px;
`

const HelpRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${cssTheme.layout.spacing.s200};
  margin-top: ${cssTheme.layout.spacing.s500};
`

export interface PriceRangeBarSkeletonProps {
  showHelpHint?: boolean
  /** Optional node rendered inside the tooltip (e.g. Vio logo). Matches the
   *  live bar's `tooltipIconSlot`, so the icon stays visible during loading
   *  and only the price text is skeletonised. */
  tooltipIconSlot?: ReactNode
  dataTestId?: string
}

/**
 * Loading placeholder for `PriceRangeBar`. Renders the static frame as real
 * (segment colours, Low/Fair/High labels, help text) and only skeletons the
 * data-derived pieces — the tooltip price chip and the four boundary price
 * values. Matches the real bar's row heights and positions so the card
 * reserves identical space.
 *
 * Rendered while `usePriceHistory` is in flight or its data is unavailable.
 * Used inside `PriceTrendCompact` (compact) and `PriceTrendDetailContent`
 * (full) in place of the live bar.
 */
export const PriceRangeBarSkeleton: React.FC<PriceRangeBarSkeletonProps> = ({
  showHelpHint = true,
  tooltipIconSlot,
  dataTestId = 'price-range-bar-skeleton'
}) => {
  const {formatMessage} = useIntl()
  const lowLabel = formatMessage(
    priceTrendMessages.priceInsightsDealscoreSegmentLow
  )
  const fairLabel = formatMessage(
    priceTrendMessages.priceInsightsDealscoreSegmentFair
  )
  const highLabel = formatMessage(
    priceTrendMessages.priceInsightsDealscoreSegmentHigh
  )
  const helpText = formatMessage(
    priceTrendMessages.priceInsightsDealscoreFooterLabel
  )

  return (
    <Root data-id={dataTestId}>
      <TooltipRow>
        <TooltipAnchor>
          <TooltipBubble>
            {tooltipIconSlot}
            <TooltipPriceSlot>
              <SkeletonLoader />
            </TooltipPriceSlot>
          </TooltipBubble>
          <TooltipTail />
        </TooltipAnchor>
      </TooltipRow>
      <BarTrack>
        <Segment position="low" color={SEGMENT_COLORS.low} />
        <Segment position="fair" color={SEGMENT_COLORS.fair} />
        <Segment position="high" color={SEGMENT_COLORS.high} />
      </BarTrack>
      <LabelsRow>
        <Label>
          <Text variant="titleXS" colorPath="content.neutral.primary">
            {lowLabel}
          </Text>
        </Label>
        <Label>
          <Text variant="titleXS" colorPath="content.neutral.primary">
            {fairLabel}
          </Text>
        </Label>
        <Label>
          <Text variant="titleXS" colorPath="content.neutral.primary">
            {highLabel}
          </Text>
        </Label>
      </LabelsRow>
      <PricesRow>
        <PriceCell align="start" column={1}>
          <SkeletonLoader />
        </PriceCell>
        <PriceCell align="start" column={2}>
          <SkeletonLoader />
        </PriceCell>
        <PriceCell align="start" column={3}>
          <SkeletonLoader />
        </PriceCell>
        <PriceCell align="end" column={3}>
          <SkeletonLoader />
        </PriceCell>
      </PricesRow>
      {showHelpHint && (
        <HelpRow>
          <Box display="flex" alignItems="center">
            <Icon
              name="HelpCircle"
              size="sm"
              colorPath="content.neutral.primary"
            />
          </Box>
          <Text variant="bodyXS" colorPath="content.neutral.primary">
            {helpText}
          </Text>
        </HelpRow>
      )}
    </Root>
  )
}
